/**
 * Canonical menu item definitions for each panel.
 * IDs are stable integers stored in role_menu_access.menu_ids.
 * Each panel uses its own ID namespace — IDs only need to be unique within a panel.
 *
 * panel_type is determined from role_value:
 *   role 1  → 'user'       (User panel)
 *   role 2  → 'org'        (Org Admin panel)
 *   role >2 → 'instructor' (Instructor panel — custom roles)
 */

export const getPanelTypeForRole = (roleValue) => {
  const v = parseInt(roleValue);
  if (v === 1) return 'user';
  if (v === 2) return 'org';
  if (v > 2)  return 'instructor';
  return 'admin'; // role 0 — Super Admin (not configurable)
};

// ---------------------------------------------------------------------------
// User Panel
// ---------------------------------------------------------------------------
export const USER_MENU_ITEMS = [
  { id: 0, label: 'Dashboard' },
  { id: 1, label: 'Browse Courses' },
  { id: 2, label: 'My Courses' },
  { id: 7, label: 'My Assessments' },
  { id: 10, label: 'My Event Calendar' },
  { id: 3, label: 'My Virtual-Led Training' },
  { id: 4, label: 'My Certificates' },
  { id: 5, label: 'Forum Discussions' },
  { id: 6, label: 'Surveys' },
  { id: 16, label: 'Learning Journey' },
  { id: 8, label: 'My Transactions' },
  { id: 9, label: 'Support Tickets' },
  { id: 11, label: 'Leaderboard' },
  { id: 12, label: 'Badges' },
];

// ---------------------------------------------------------------------------
// Org Admin / Instructor Panel
// ---------------------------------------------------------------------------
export const ORG_MENU_ITEMS = [
  { id: 0,  label: 'Dashboard' },
  { id: 1,  label: 'Manage Courses' },
  { id: 2,  label: 'Manage Users' },
  { id: 3,  label: 'Manage Admins' },
  { id: 4,  label: 'Manage Licenses' },
  { id: 5,  label: 'Forum Discussions' },
  { id: 6,  label: 'Virtual-Led Training' },
  { id: 11, label: 'Event Calendar' },
  { id: 7,  label: 'Certificate Templates' },
  { id: 8,  label: 'Manage Surveys' },
  { id: 13, label: 'Manage Assessments' },
  { id: 9,  label: 'Support Tickets' },
  { id: 10, label: 'Reports Analytics' },
  { id: 12, label: 'Leaderboard' },
];

// ---------------------------------------------------------------------------
// Instructor Panel (custom roles — role_value > 2)
// IDs match InstructorSidebar.jsx ALL_MENU_ITEMS
// ---------------------------------------------------------------------------
export const INSTRUCTOR_MENU_ITEMS = [
  { id: 0, label: 'Dashboard' },
  { id: 1, label: 'My Courses' },
  { id: 2, label: 'My Users' },
  { id: 3, label: 'Forum Discussions' },
  { id: 4, label: 'Virtual-Led Training' },
  { id: 8, label: 'Event Calendar' },
  { id: 5, label: 'Surveys' },
  { id: 10, label: 'Assessments' },
  { id: 6, label: 'Support Tickets' },
  { id: 7, label: 'Reports Analytics' },
  { id: 9, label: 'Leaderboard' },
];

// ---------------------------------------------------------------------------
// Super Admin Panel (reference only — Super Admin always has full access)
// ---------------------------------------------------------------------------
export const ADMIN_MENU_ITEMS = [
  { id: 0,  label: 'Dashboard',                group: null },
  { id: 1,  label: 'Manage Users',             group: null },
  { id: 2,  label: 'Manage Courses',           group: null },
  { id: 4,  label: 'Manage Organizations',     group: null },
  { id: 5,  label: 'Manage Licenses',          group: null },
  { id: 6,  label: 'Forum Discussions',        group: null },
  { id: 8,  label: 'Virtual-Led Training',   group: null },
  { id: 20, label: 'Event Calendar',         group: null },
  { id: 10, label: 'Manage Surveys',           group: null },
  { id: 11, label: 'Manage Support Tickets',   group: null },
  { id: 15, label: 'Manage Assessments',       group: null },
  { id: 23, label: 'Manage Learning Journeys',      group: null },
  { id: 13, label: 'Manage Vouchers',          group: null },
  { id: 14, label: 'Reports Analytics',        group: null },
  { id: 3,  label: 'Manage Skills',            group: 'Master Setup' },
  { id: 17, label: 'Manage Group',             group: 'Master Setup' },
  { id: 18, label: 'Manage Role',              group: 'Master Setup' },
  { id: 7,  label: 'Flagged Comments',         group: 'Master Setup' },
  { id: 9,  label: 'Certificate Templates',    group: 'Master Setup' },
  { id: 21, label: 'Global Learning Score & Badges', group: 'Master Setup' },
  { id: 22, label: 'EduGenie', group: 'Master Setup' },
  { id: 12, label: 'Site Management',          group: null },
];

export const getMenuItemsForRole = (roleValue) => {
  const panel = getPanelTypeForRole(roleValue);
  if (panel === 'user')       return USER_MENU_ITEMS;
  if (panel === 'org')        return ORG_MENU_ITEMS;
  if (panel === 'instructor') return INSTRUCTOR_MENU_ITEMS;
  return ADMIN_MENU_ITEMS;
};
